import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ArrowLeft,
  Printer,
  User,
  Calendar,
  CreditCard,
  Hash,
  Tag,
  FileText,
  ExternalLink,
} from 'lucide-react'
import { useInvoice } from '@/hooks/useInvoices'
import { formatCurrency, maskNRIC } from '@/lib/utils'

export const Route = createFileRoute('/invoices_/$id')({
  component: InvoiceDetailPage,
})

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Paid: { bg: '#10b981', text: 'white' },
  Pending: { bg: '#f59e0b', text: 'white' },
  Void: { bg: '#ef4444', text: 'white' },
}

const PATIENT_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  New: { bg: '#3b82f6', text: 'white' },
  Existing: { bg: '#8b5cf6', text: 'white' },
}

function InvoiceDetailPage() {
  const navigate = useNavigate()
  const { id } = Route.useParams()
  const invoiceId = Number(id)

  const { data, isPending } = useInvoice(invoiceId)

  const invoice = data?.data
  const items = data?.items ?? []
  const itemStats = data?.itemStats

  const subtitle = invoice
    ? `${new Date(invoice.invoiceDate).toLocaleDateString('en-MY', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })} · ${invoice.branchName}`
    : 'Loading...'

  return (
    <div className="flex flex-col h-full">
      <Header
        title={isPending ? 'Invoice Details' : invoice?.invoiceNo || 'Invoice Details'}
        subtitle={subtitle}
      />

      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {/* Top bar: back + print */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 text-text-muted hover:text-text-dark"
            onClick={() => navigate({ to: '/invoices' })}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Invoices
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>

        {isPending ? (
          <div className="space-y-6 max-w-3xl">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : invoice ? (
          <div className="space-y-6 max-w-3xl">
            {/* Status Badges */}
            <div className="flex gap-2">
              {invoice.status && (
                <Badge
                  style={{
                    background: STATUS_COLORS[invoice.status]?.bg || '#6b7280',
                    color: STATUS_COLORS[invoice.status]?.text || 'white',
                  }}
                >
                  {invoice.status}
                </Badge>
              )}
              {invoice.patientType && (
                <Badge
                  style={{
                    background: PATIENT_TYPE_COLORS[invoice.patientType]?.bg || '#6b7280',
                    color: PATIENT_TYPE_COLORS[invoice.patientType]?.text || 'white',
                  }}
                >
                  {invoice.patientType} Patient
                </Badge>
              )}
            </div>

            {/* Amount Summary */}
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-4xl font-bold text-center">
                {formatCurrency(parseFloat(invoice.grandTotal || '0'))}
              </div>
              <div className="text-sm text-muted-foreground text-center mt-1">
                Grand Total
              </div>

              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="text-center">
                  <div className="text-xl font-semibold">
                    {formatCurrency(parseFloat(invoice.servicesAmount || '0'))}
                  </div>
                  <div className="text-xs text-muted-foreground">Services</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-semibold">
                    {formatCurrency(parseFloat(invoice.inventoryAmount || '0'))}
                  </div>
                  <div className="text-xs text-muted-foreground">Inventory</div>
                </div>
              </div>

              {parseFloat(invoice.discountAmount || '0') > 0 && (
                <div className="text-center mt-4 pt-4 border-t border-border">
                  <div className="text-xl font-semibold text-red-500">
                    -{formatCurrency(parseFloat(invoice.discountAmount || '0'))}
                  </div>
                  <div className="text-xs text-muted-foreground">Discount</div>
                </div>
              )}
            </div>

            {/* Patient Info */}
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium">Patient Information</h3>
                {invoice.patientId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-primary"
                    onClick={() =>
                      navigate({
                        to: '/patients/$id',
                        params: { id: String(invoice.patientId) },
                      })
                    }
                  >
                    View Patient
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <InfoRow icon={User} label="Name" value={invoice.patientName || 'Unknown'} />
                <InfoRow icon={Hash} label="PNO" value={invoice.patientPno || '-'} />
                {invoice.patientPhone && (
                  <InfoRow icon={CreditCard} label="Phone" value={invoice.patientPhone} />
                )}
                {invoice.patientNric && (
                  <InfoRow icon={FileText} label="NRIC" value={maskNRIC(invoice.patientNric)} />
                )}
              </div>
            </div>

            {/* Invoice Details */}
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-medium mb-4">Invoice Details</h3>
              <div className="space-y-2">
                <InfoRow
                  icon={Calendar}
                  label="Date"
                  value={
                    invoice.invoiceDate
                      ? new Date(invoice.invoiceDate).toLocaleDateString('en-MY', {
                          weekday: 'long',
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })
                      : '-'
                  }
                />
                <InfoRow icon={CreditCard} label="Payment Method" value={invoice.paymentMethod || '-'} />
                <InfoRow icon={User} label="Created By" value={invoice.createdBy || '-'} />
              </div>
            </div>

            {/* Invoice Items — NO scroll cap */}
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium">
                  Items ({itemStats?.totalItems ?? items.length})
                </h3>
                {itemStats && (itemStats.totalFoc > 0 || itemStats.totalPromo > 0) && (
                  <div className="flex gap-2">
                    {itemStats.totalFoc > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {itemStats.totalFoc} FOC
                      </Badge>
                    )}
                    {itemStats.totalPromo > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {itemStats.totalPromo} Promo
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start justify-between rounded-lg border p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {item.rawTreatmentName}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          Qty: {parseFloat(item.quantity || '1')}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          × {formatCurrency(parseFloat(item.unitPrice || '0'))}
                        </span>
                        {item.promoCode && (
                          <Badge variant="outline" className="text-xs py-0">
                            <Tag className="w-3 h-3 mr-1" />
                            {item.promoCode}
                          </Badge>
                        )}
                        {item.isFoc && (
                          <Badge variant="secondary" className="text-xs py-0">
                            FOC
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="font-medium">
                        {formatCurrency(parseFloat(item.totalPrice || '0'))}
                      </div>
                      {parseFloat(item.discount || '0') > 0 && (
                        <div className="text-xs text-red-500">
                          -{formatCurrency(parseFloat(item.discount || '0'))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {items.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No items found
                  </div>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-medium mb-4">Payment Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(parseFloat(invoice.subtotal || '0'))}</span>
                </div>
                {parseFloat(invoice.discountAmount || '0') > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="text-red-500">
                      -{formatCurrency(parseFloat(invoice.discountAmount || '0'))}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span>Grand Total</span>
                  <span>{formatCurrency(parseFloat(invoice.grandTotal || '0'))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Paid</span>
                  <span className="text-green-600">
                    {formatCurrency(parseFloat(invoice.paidAmount || '0'))}
                  </span>
                </div>
                {parseFloat(invoice.balance || '0') > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Balance</span>
                    <span className="text-orange-500">
                      {formatCurrency(parseFloat(invoice.balance || '0'))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Invoice not found
          </div>
        )}
      </div>
    </div>
  )
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  )
}
